import { useNavigate } from "react-router-dom";
import { useContext } from "react";

import createLink from "../libs/createLink";
import { ModalContext } from "../ui/components2/ModalManager";
import useAbility from "./useAbility";
import useCurrentUser from "./useCurrentUser";
import { useDrawer } from "../ui/components2/DrawerProvider";
import ContentLocked from "../ui/components2/ContentLocked/ContentLocked";
import OffersModal, { OFFERS_MODAL_CONTEXT, OFFERS_DRAWER_OPTIONS } from "../ui/components2/OffersModal/OffersModal";
import CONTENT_RESTRICTION_RULE from "../const/contentRestrictionRule";
import USER_ROLES from "../const/userRoles";
import { isCertifEligibleGender } from "../const/certification";
import { STORY_QUOTA_KIND, hasStoryQuotaLeft, isStoryQuotaExhausted } from "../libs/storyQuota";

export const CURRENT_USER_LOCKED_LABEL = {
  ROLE_GUEST: "ROLE_GUEST",
  ROLE_EMAIL_UNCONFIRMED: "ROLE_EMAIL_UNCONFIRMED",
  ROLE_BANNED: "ROLE_BANNED",
  ROLE_NO_ACCESS: "ROLE_NO_ACCESS",
  ROLE_NO_PREMIUM: "ROLE_NO_PREMIUM",
  USER_WARNING: "USER_WARNING",
  ROLE_CAN_BE_PRIVILEGED: "ROLE_CAN_BE_PRIVILEGED",
};

// Valeurs « expérience free » appliquées quand la capability de levée de quota est absente
// (guest / gratuit / non grandfathered). Miroir de const/freeLimits.js côté API : le tiering
// (qui est débloqué) est une capability ; ces nombres ne sont que la valeur du plafond.
const FREE_VIDEO_LIMIT_S = 15;
const FREE_BOOKMARKS_LIMIT = 20;
const FREE_NOTIFICATIONS_LIMIT = 10;

/**
 * ✅ Source UNIQUE des droits : subscription.capabilities, résolu par l'API
 * (resolveCapabilities). Le front ne recalcule plus l'éligibilité premium/plan/certif :
 * il lit la capability correspondant à l'action. Garantit display == enforcement.
 * Mapping action (règle de restriction) -> clé de capability.
 */
const RULE_TO_CAPABILITY = {
  [CONTENT_RESTRICTION_RULE.REPOST_CONTENT]: "canRepost",
  [CONTENT_RESTRICTION_RULE.CREATE_POLL]: "canCreatePoll",
  [CONTENT_RESTRICTION_RULE.CREATE_EPHEMERAL_STORY]: "canCreateStory",
  [CONTENT_RESTRICTION_RULE.VIEW_EPHEMERAL_STORY]: "canViewStories",
  [CONTENT_RESTRICTION_RULE.NATIVE_FULLSCREEN]: "canUseFullscreen",
  [CONTENT_RESTRICTION_RULE.DARK_MODE]: "canUseDarkMode",
  [CONTENT_RESTRICTION_RULE.INCOGNITO]: "canUseIncognito",
  [CONTENT_RESTRICTION_RULE.MESSENGER]: "canUseMessenger",
  [CONTENT_RESTRICTION_RULE.MEMBERS_FILTER]: "canFilterMembers",
  [CONTENT_RESTRICTION_RULE.VIEW_LAST_VISITORS]: "canViewLastVisitors",
  [CONTENT_RESTRICTION_RULE.ADD_TO_CIRCLE]: "canAddToCircle",
};

// Capabilities que la CERTIFICATION débloque, et pour lesquelles on propose donc « certifiez-vous
// gratuitement » (ROLE_CAN_BE_PRIVILEGED) plutôt que l'abonnement. Deux familles :
//   • le relationnel Max, accordé PLEINEMENT par la certif vidéo ;
//   • les stories, accordées de façon PLAFONNÉE (N/jour) par la certification.
// Les stories restent des features Voissa Plus : la certif en ouvre un avant-goût quotidien, ce
// qui suffit à en faire un argument — la modale certif est plus convertissante qu'un mur payant.
const CERTIFIABLE_CAPABILITIES = [
  "canUseMessenger",
  "canFilterMembers",
  "canViewLastVisitors",
  "canAddToCircle",
  "canCreateStory",
  "canViewStories",
];

// Règle -> nature de quota de story. Une règle listée ici n'est PAS bloquée par la seule absence
// de la capability pleine : la capability quotaée suffit, tant qu'il reste du quota du jour.
const RULE_TO_STORY_QUOTA_KIND = {
  [CONTENT_RESTRICTION_RULE.CREATE_EPHEMERAL_STORY]: STORY_QUOTA_KIND.PUBLISH,
  [CONTENT_RESTRICTION_RULE.VIEW_EPHEMERAL_STORY]: STORY_QUOTA_KIND.VIEW,
};

// true si l'action est gatée par une capability et que l'utilisateur ne l'a pas.
// Pour les stories, « ne l'a pas » signifie : ni la cap pleine, ni du quota restant — un certifié
// qui a encore sa story du jour n'est donc pas restreint.
const lacksCapabilityForAction = (user, action) => {
  const quotaKind = RULE_TO_STORY_QUOTA_KIND[action];
  if (quotaKind) return !hasStoryQuotaLeft(user, quotaKind);
  const cap = RULE_TO_CAPABILITY[action];
  if (!cap) return false;
  return !user?.capabilities?.[cap];
};

const MODERATED_RESTRICTION_CHECK = {
  [CONTENT_RESTRICTION_RULE.CREATE_PUBLICATION_MEDIA]: (user) => {
    return !!user?.restrictions?.blockMediaCreation;
  },
  // Une story est une publication : la sanction "ban publication" (blockMediaCreation) doit aussi
  // bloquer sa création → clic "créer une story" ouvre la modale de restriction, comme pour un média.
  [CONTENT_RESTRICTION_RULE.CREATE_EPHEMERAL_STORY]: (user) => {
    return !!user?.restrictions?.blockMediaCreation;
  },
};

export const useCurrentUserLockedState = ({ action }) => {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const currentRole = currentUser?.role || "guest";
  const { openModal } = useContext(ModalContext);

  // Capability gatant l'action (le cas échéant) + son absence pour cet utilisateur.
  const capability = RULE_TO_CAPABILITY[action];
  const lacksCapability = lacksCapabilityForAction(currentUser, action);
  // Troisième état (stories uniquement) : le droit EXISTE mais la journée est consommée. Il faut
  // le distinguer, sinon on proposerait « certifiez votre profil » à quelqu'un qui l'est déjà.
  const storyQuotaKind = RULE_TO_STORY_QUOTA_KIND[action];
  const quotaExhausted = !!storyQuotaKind && isStoryQuotaExhausted(currentUser, storyQuotaKind);

  const currentUserLockConditions = [
    // Guest
    {
      label: CURRENT_USER_LOCKED_LABEL.ROLE_GUEST,
      condition: (user, role) => !user?._id && role === "guest",
      defaultHandler: (contentOwner, lockedAction) => {
        openModal("Signin", {
          lockedContentOwner: contentOwner,
          lockedReason: CURRENT_USER_LOCKED_LABEL.ROLE_GUEST,
          lockedAction,
        });
      },
    },

    // User warning / moderated restrictions
    {
      label: CURRENT_USER_LOCKED_LABEL.USER_WARNING,
      condition: (user) =>
        user?.restrictions?.blockAllContentCreation ||
        MODERATED_RESTRICTION_CHECK[action]?.(user),
      defaultHandler: () => {
        openModal("ModalUserRestrictions", {
          toggle: true,
          isModal: true,
          userId: currentUser?._id,
          username: currentUser?.username,
        });
      },
    },

    // Email unconfirmed
    {
      label: CURRENT_USER_LOCKED_LABEL.ROLE_EMAIL_UNCONFIRMED,
      condition: (_user, role) => role === "emailUnconfirmed",
      defaultHandler: () => {
        openModal("EncourageEmailConfirm", { forceOpen: true });
      },
    },

    // Banned
    {
      label: CURRENT_USER_LOCKED_LABEL.ROLE_BANNED,
      condition: (_user, role) => role === "banned",
      defaultHandler: () => {
        navigate(createLink("signin"));
      },
    },

    // Can be privileged (certif) — femmes/couples éligibles à la certification vidéo
    // (alternative gratuite aux features relationnelles Max). Ne s'applique pas si la
    // capability est déjà accordée (Max/certif actifs => capability true => pas de lock).
    // Pas de `defaultHandler` : ce label fait partie de `isPaywallLocked`, donc les handlers
    // ouvrent toujours OffersModal (qui porte l'alternative certif via `canBePriviliged`).
    {
      label: CURRENT_USER_LOCKED_LABEL.ROLE_CAN_BE_PRIVILEGED,
      // `!quotaExhausted` : un membre DÉJÀ certifié qui a simplement épuisé sa story du jour ne
      // doit pas se voir proposer de se certifier. Il tombe sur ROLE_NO_PREMIUM ci-dessous, dont
      // la modale porte la copie « quota atteint » (cf. triggerAction *Daily).
      condition: (user) =>
        isCertifEligibleGender(user?.profile?.gender) &&
        !quotaExhausted &&
        (capability
          ? lacksCapability && CERTIFIABLE_CAPABILITIES.includes(capability)
          : !user?.subscription?.hasAccess),
    },

    /**
     * ✅ Accès global: hasAccess
     * -> uniquement pour les actions NON gatées par une capability (vidéo, favoris,
     *    notifications, limites de profil…). Les actions à capability sont traitées
     *    par ROLE_NO_PREMIUM ci-dessous (source unique = subscription.capabilities).
     */
    {
      label: CURRENT_USER_LOCKED_LABEL.ROLE_NO_ACCESS,
      condition: () => !capability && !currentUser?.subscription?.hasAccess,
      defaultHandler: () => {
        navigate(createLink("subscription", currentUser));
      },
    },

    /**
     * ✅ Capability manquante (paywall premium)
     * -> s'applique aux actions gatées par une capability (Plus ou Max). La capability
     *    encapsule déjà tout (isPremium / plan / certif / grandfathering) côté serveur.
     */
    {
      label: CURRENT_USER_LOCKED_LABEL.ROLE_NO_PREMIUM,
      condition: () => !!capability && lacksCapability,
      defaultHandler: () => {
        navigate(createLink("subscription", currentUser));
      },
    },
  ];

  return currentUserLockConditions.find(({ condition }) =>
    condition(currentUser, currentRole)
  );
};

const useRestriction = (action, context, options = {}) => {
  const { cannot } = useAbility();
  const { openDrawer, closeDrawer } = useDrawer();
  const currentUser = useCurrentUser();
  const { openModal } = useContext(ModalContext);
  const currentUserLockedState = useCurrentUserLockedState({ action });

  const { restrictcionDisabled = false } = options;
  if (restrictcionDisabled) return { isRestricted: false };

  const label = currentUserLockedState?.label;
  const isUserSignupAfterNewRestriction =
    currentUser && new Date("2024-10-26") < new Date(currentUser.createdAt);
  const isEmailUnconfirmed = currentUser?.role === USER_ROLES.EMAIL_UNCONFIRMED;

  const isPaywallLocked = [
    CURRENT_USER_LOCKED_LABEL.ROLE_CAN_BE_PRIVILEGED,
    CURRENT_USER_LOCKED_LABEL.ROLE_NO_ACCESS,
    CURRENT_USER_LOCKED_LABEL.ROLE_NO_PREMIUM, // ne peut exister que sur actions premium-only
  ].includes(label);

  const canBePriviliged = label === CURRENT_USER_LOCKED_LABEL.ROLE_CAN_BE_PRIVILEGED;
  const isCertified = currentUser?.isCertified;

  // Capability déclenchante de la modale, pour les stories. Deux copies très différentes selon
  // l'état : « vous n'avez pas ce droit » (on vend la certif ou l'abonnement) vs « vous l'avez
  // épuisé aujourd'hui » (on ne revend rien, on explique le plafond et on propose de le lever).
  const storyTrigger = (kind, fullCap, dailyCap) =>
    isStoryQuotaExhausted(currentUser, kind) ? dailyCap : fullCap;

  // Ouvre la modale d'offres centralisée (contexte « action bloquée »). `capability` est
  // la clé déclenchante (canRepost, canUseMessenger…) ; OffersModal en déduit l'offre (Plus/Max)
  // et, pour les features relationnelles Max, propose la certif si l'utilisateur y est éligible.
  const openOffersDrawer = (capability, ctx = {}) =>
    openDrawer(
      <OffersModal
        context={OFFERS_MODAL_CONTEXT.BLOCKED}
        triggerAction={capability}
        username={ctx.username}
        canBePriviliged={canBePriviliged}
        isCertified={isCertified}
        inDrawer
        handleCancel={closeDrawer}
      />,
      // Depuis la refonte « Premium d'abord » (09/2026), TOUS les gabarits de blocage sont en
      // colonne — bloqué Max, bloqué Plus et certification. Le drawer large ne servait qu'au
      // comparatif deux cartes, qui n'est plus la réponse à une action bloquée : dans un drawer
      // large, la colonne flottait au milieu d'une bande blanche.
      OFFERS_DRAWER_OPTIONS
    );

  const callDefaultHandler = (...args) => {
    if (currentUserLockedState?.defaultHandler) {
      currentUserLockedState.defaultHandler(...args);
    }
  };

  const handlers = {
    [CONTENT_RESTRICTION_RULE.CREATE_PUBLICATION_MEDIA]: () => {
      const cannotUploadMedia = cannot({ action: "upload", subject: "Media" });

      // locks "auth/modération" seulement
      const localLockedState = [
        CURRENT_USER_LOCKED_LABEL.ROLE_GUEST,
        CURRENT_USER_LOCKED_LABEL.USER_WARNING,
        CURRENT_USER_LOCKED_LABEL.ROLE_EMAIL_UNCONFIRMED,
        CURRENT_USER_LOCKED_LABEL.ROLE_BANNED,
      ].includes(label);

      const isRestricted = localLockedState || cannotUploadMedia;

      return {
        isRestricted,
        restrictionReason: label || (cannotUploadMedia ? "CANNOT_UPLOAD_MEDIA" : undefined),
        restrictionHandler: () => {
          if (localLockedState) {
            callDefaultHandler();
            return;
          }

          // lock CASL upload media (si tu veux un drawer dédié)
          if (cannotUploadMedia) {
            openDrawer(
              <ContentLocked
                restrictionRule={CONTENT_RESTRICTION_RULE.CREATE_PUBLICATION_MEDIA}
                context={{ caslLocked: true }}
                inDrawer
                handleCancel={closeDrawer}
              />,
              { small: true }
            );
          }
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.ADD_TO_CIRCLE]: (contentOwner) => {
      const isRestricted = cannot({ action: "create", subject: "Friend" }) || !!currentUserLockedState;

      return {
        isRestricted,
        restrictionReason: label || "CANNOT_CREATE_FRIEND",
        restrictionHandler: (e) => {
          if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openOffersDrawer("canAddToCircle");
            return;
          }

          callDefaultHandler(contentOwner, action);
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.MEMBERS_FILTER]: () => {
      const isRestricted = isEmailUnconfirmed || isPaywallLocked;

      return {
        isRestricted,
        restrictionReason: "CANNOT_FILTER_MEMBERS",
        restrictionHandler: (e) => {
          if (isEmailUnconfirmed) {
            if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
            openModal("EncourageEmailConfirm", { forceOpen: true });
            return;
          }

          if (isPaywallLocked) {
            if (isRestricted &&  e?.nativeEvent instanceof Event) e.preventDefault();
            openOffersDrawer("canFilterMembers");
          }
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.ENCOURAGE_GAME]: () => {
      return {
        isRestricted: isEmailUnconfirmed,
        restrictionReason: isEmailUnconfirmed
          ? CURRENT_USER_LOCKED_LABEL.ROLE_EMAIL_UNCONFIRMED
          : undefined,
        restrictionHandler: () => {
          if (isEmailUnconfirmed) {
            openModal("EncourageEmailConfirm", { forceOpen: true });
          }
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.MESSENGER]: (contentOwner = {}) => {
      const isOwnerAdminOrMod = [USER_ROLES.ADMINISTRATOR, USER_ROLES.MODERATOR].includes(
        contentOwner?.role
      );

      const isRestricted = !isOwnerAdminOrMod && !!currentUserLockedState;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionHandler: (e) => {
          if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openOffersDrawer("canUseMessenger", { username: contentOwner?.username });
            return;
          }

          callDefaultHandler(contentOwner, action);
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.VIEW_LAST_VISITORS]: () => {
      const isRestricted = !!currentUserLockedState && isUserSignupAfterNewRestriction;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionHandler: () => {
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openOffersDrawer("canViewLastVisitors");
            return;
          }

          callDefaultHandler();
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.VIDEO_DURATION_LIMIT]: ({ media }) => {
      // Propriétaire : jamais limité.
      if (media?.owner?._id === currentUser?._id) {
        return { isRestricted: false };
      }

      // Capability de levée : canWatchFullVideos (Voissa Plus/Max OU compte ancien grandfathered,
      // résolu serveur). Absente (guest inclus) => plafond FREE_VIDEO_LIMIT_S.
      const limitS = currentUser?.capabilities?.canWatchFullVideos ? null : FREE_VIDEO_LIMIT_S;
      if (limitS == null) return { isRestricted: false };

      // Ne s'applique qu'aux vidéos de plus de 30 jours.
      const videoAgeRestrictionDays = 30;
      const videoCreatedAt = new Date(media?.createdAt);
      const restrictionCutoff = new Date();
      restrictionCutoff.setDate(restrictionCutoff.getDate() - videoAgeRestrictionDays);
      if (videoCreatedAt >= restrictionCutoff) return { isRestricted: false };

      const isLoggedIn = !!currentUser?._id;

      return {
        isRestricted: true,
        restrictionReason: label,
        restrictionHandler: () => {
          // Membre connecté : quota levé par canWatchFullVideos -> modale d'offres centralisée.
          // Invité : gate d'inscription (ContentLocked, variante signup).
          if (isLoggedIn) {
            openOffersDrawer("canWatchFullVideos");
            return;
          }
          openDrawer(
            <ContentLocked
              restrictionRule={CONTENT_RESTRICTION_RULE.VIDEO_DURATION_LIMIT}
              context={{ videoAgeRestrictionDays, premium: false }}
              inDrawer
              handleCancel={closeDrawer}
            />,
            { small: true }
          );
        },
        restrictionParams: {
          duration: limitS,
          videoAgeRestrictionDays,
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.USER_BOOKMARKS_LIMIT]: () => {
      // Quota de COMPTE : ne concerne qu'un membre connecté (un invité a un gate de login distinct).
      // Capability de levée : canSaveUnlimitedBookmarks. Absente => plafond FREE_BOOKMARKS_LIMIT.
      const restricted = !!currentUser?._id && !currentUser?.capabilities?.canSaveUnlimitedBookmarks;
      return {
        isRestricted: restricted,
        restrictionReason: label,
        restrictionParams: { limit: restricted ? FREE_BOOKMARKS_LIMIT : null },
      };
    },

    [CONTENT_RESTRICTION_RULE.USER_NOTIFICATIONS_LIMIT]: () => {
      // Quota de COMPTE : connecté uniquement. Capability de levée : canViewAllNotifications.
      const restricted = !!currentUser?._id && !currentUser?.capabilities?.canViewAllNotifications;
      return {
        isRestricted: restricted,
        restrictionReason: label,
        restrictionParams: { limit: restricted ? FREE_NOTIFICATIONS_LIMIT : null },
      };
    },

    [CONTENT_RESTRICTION_RULE.PROFIL_FEED_PUBLICATIONS_LIMIT]: ({ contentOwner }) => {
      if (!contentOwner || contentOwner?._id === currentUser?._id) return { isRestricted: false };

      const publicationLimitPremium = 40;
      const publicationLimitGuest = 10;

      const guestLocked = label === CURRENT_USER_LOCKED_LABEL.ROLE_GUEST;

      return {
        isRestricted: false, // tu l’avais désactivé
        restrictionReason: label,
        restrictionParams: {
          limit: guestLocked ? publicationLimitGuest : publicationLimitPremium,
          isGuestLocked: guestLocked,
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.PROFILE_MEDIAS_LIMIT]: ({ contentOwner }) => {
      if (!contentOwner || contentOwner?._id === currentUser?._id) return { isRestricted: false };

      const publicationLimitPremium = 20;
      const publicationLimitGuest = 10;

      const guestLocked = label === CURRENT_USER_LOCKED_LABEL.ROLE_GUEST;

      return {
        isRestricted: false, // tu l’avais désactivé
        restrictionReason: label,
        restrictionParams: {
          limit: guestLocked ? publicationLimitGuest : publicationLimitPremium,
          isGuestLocked: guestLocked,
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.PROFIL_COLLECTION_MEDIAS_LIMIT]: ({ contentOwner }) => {
      if (!contentOwner || contentOwner?._id === currentUser?._id) return { isRestricted: false };

      const publicationLimitGuest = 10;
      const guestLocked = label === CURRENT_USER_LOCKED_LABEL.ROLE_GUEST;

      return {
        isRestricted: false, // tu l’avais désactivé
        restrictionReason: label,
        restrictionParams: {
          limit: publicationLimitGuest,
          isGuestLocked: guestLocked,
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.FOLLOW_MEMBER]: (contentOwner = {}) => {
      const isGuest = !currentUser?._id;
      const onlyContributorsCanFollowMe = !!contentOwner?.setting?.onlyContributorsCanFollowMe;
      const contributorGate = onlyContributorsCanFollowMe && !currentUser?.isContributor;
      const isEmailUnconfirmedLocked =
        label === CURRENT_USER_LOCKED_LABEL.ROLE_EMAIL_UNCONFIRMED;

      const isRestricted = isGuest || isEmailUnconfirmedLocked || contributorGate;

      return {
        isRestricted,
        restrictionReason: isGuest
          ? CURRENT_USER_LOCKED_LABEL.ROLE_GUEST
          : contributorGate
            ? "CONTRIBUTOR_GATE"
            : undefined,
        restrictionHandler: (e) => {
          if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
          if (!isRestricted) return;

          if (isGuest) {
            openModal("Signin", {
              lockedContentOwner: contentOwner,
              lockedReason: CURRENT_USER_LOCKED_LABEL.ROLE_GUEST,
              lockedAction: action,
            });
            return;
          }

          if (isEmailUnconfirmedLocked) {
            openModal("EncourageEmailConfirm", { forceOpen: true });
            return;
          }

          if (!contributorGate) return;

          openDrawer(
            <ContentLocked
              restrictionRule={CONTENT_RESTRICTION_RULE.FOLLOW_MEMBER}
              context={{ contributorGate: true, username: contentOwner?.username }}
              inDrawer
              handleCancel={closeDrawer}
            />,
            { small: true }
          );
        },
      };
    },

    /**
     * ✅ REPOST_CONTENT = Premium-only (si tu le veux)
     * - si pas hasAccess => ROLE_NO_ACCESS
     * - si hasAccess mais pas premium => ROLE_NO_PREMIUM
     * => aucun impact sur les hasAccess sauf si tu demandes premium (ici oui).
     */
    [CONTENT_RESTRICTION_RULE.REPOST_CONTENT]: ({ contentOwner } = {}) => {
      const isRestricted = !!currentUserLockedState;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionHandler: (e) => {
          if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openOffersDrawer("canRepost", { username: contentOwner?.username });
            return;
          }

          callDefaultHandler(contentOwner, action);
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.CREATE_POLL]: ({ contentOwner, enable = true } = {}) => {
      const isRestricted = enable ? !!currentUserLockedState : false;

      return {
        enable,
        isRestricted,
        restrictionReason: label,
        restrictionHandler: (e) => {
          if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
          if (!enable) return;
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openOffersDrawer("canCreatePoll", { username: contentOwner?.username });
            return;
          }

          callDefaultHandler(contentOwner, action);
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.CREATE_EPHEMERAL_STORY]: ({ contentOwner, enable = true } = {}) => {
      // Capability = source de vérité (le serveur l'a résolue : Plus/Max/trial -> canCreateStory).
      // Le porteur d'un plan couvrant les stories n'est jamais bloqué, indépendamment de CASL.
      const cannotCreate = lacksCapabilityForAction(currentUser, CONTENT_RESTRICTION_RULE.CREATE_EPHEMERAL_STORY);
      const isRestricted = enable ? (cannotCreate || !!currentUserLockedState) : false;

      return {
        enable,
        isRestricted,
        restrictionReason: label || (cannotCreate ? "CANNOT_CREATE_EPHEMERAL_STORY" : undefined),
        restrictionHandler: (e) => {
          if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
          if (!enable) return;

          // 1. Lock spécifique non-paywall (guest, email non confirmé, banned) → handler dédié.
          if (currentUserLockedState && !isPaywallLocked) {
            callDefaultHandler(contentOwner, action);
            return;
          }

          // 2. Paywall (capability manquante / can-be-privileged) → modale d'offres centralisée.
          if (isPaywallLocked || cannotCreate) {
            openOffersDrawer(
              storyTrigger(STORY_QUOTA_KIND.PUBLISH, "canCreateStory", "canCreateStoryDaily"),
              { username: contentOwner?.username }
            );
            return;
          }

          callDefaultHandler(contentOwner, action);
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.NATIVE_FULLSCREEN]: () => {
      const isRestricted = !!currentUserLockedState;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionHandler: () => {
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openOffersDrawer("canUseFullscreen");
            return;
          }

          callDefaultHandler();
        },
      };
    },

    // Lecture des stories : cap pleine (Plus / Max / admin) OU quota journalier du certifié.
    // Le strip reste visible ; un non-porteur — ou un quotaé à sec — qui clique ouvre la modale
    // d'offres. Un invité passe par le gate d'inscription (defaultHandler du lock state).
    [CONTENT_RESTRICTION_RULE.VIEW_EPHEMERAL_STORY]: () => {
      const isRestricted = !!currentUserLockedState;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionHandler: () => {
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openOffersDrawer(storyTrigger(STORY_QUOTA_KIND.VIEW, "canViewStories", "canViewStoriesDaily"));
            return;
          }

          callDefaultHandler();
        },
      };
    },

    // Mode sombre : le toggle est affiché à tous, mais l'activation est réservée
    // aux membres disposant de la capability canUseDarkMode (Voissa Plus / Max / Admin).
    // Un membre non autorisé qui tente de l'activer déclenche la modale d'offres (Plus).
    [CONTENT_RESTRICTION_RULE.DARK_MODE]: () => {
      const isRestricted = !!currentUserLockedState;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionHandler: () => {
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openOffersDrawer("canUseDarkMode");
            return;
          }

          callDefaultHandler();
        },
      };
    },

    // Mode incognito : même traitement que le thème sombre — le sélecteur est affiché à tous, mais
    // l'activation est réservée à canUseIncognito (Voissa Max / Admin, PAS le certifié : la
    // capability n'est pas certifiable, cf. CERTIFIABLE_CAPABILITIES). Un membre non autorisé qui
    // tente d'activer ouvre la modale d'offres.
    [CONTENT_RESTRICTION_RULE.INCOGNITO]: () => {
      const isRestricted = !!currentUserLockedState;

      return {
        isRestricted,
        restrictionReason: label,
        restrictionHandler: () => {
          if (!currentUserLockedState) return;

          if (isPaywallLocked) {
            openOffersDrawer("canUseIncognito");
            return;
          }

          callDefaultHandler();
        },
      };
    },

    [CONTENT_RESTRICTION_RULE.BOOKMARK_MEDIA]: ({ media } = {}) => {
      const isGuest = !currentUser?._id;

      if (media?.isBookmarked) {
        return {
          isRestricted: false,
          restrictionReason: null,
          restrictionHandler: () => {},
        };
      }

      const onlyContributorsCanBookmark =
        (media?.canBookmark === false && media?.cannotBookmarkReason === "contributors_only") ||
        (media?.canBookmark === false && !media?.cannotBookmarkReason); // legacy

      const contributorGate = onlyContributorsCanBookmark && !currentUser?.isContributor;

      const ownerDisabledGate =
        media?.canBookmark === false &&
        media?.cannotBookmarkReason === "owner_disabled_favorites";

      const isCaslLocked = cannot({ action: "create", subject: "Bookmark" });

      const isRestricted = isGuest || ownerDisabledGate || contributorGate || isCaslLocked;

      const restrictionReason = isGuest
        ? CURRENT_USER_LOCKED_LABEL.ROLE_GUEST
        : ownerDisabledGate
          ? "OWNER_DISABLED_FAVORITES"
          : contributorGate
            ? "CONTRIBUTOR_GATE"
            : isCaslLocked
              ? "CANNOT_CREATE_BOOKMARK"
              : undefined;

      return {
        isRestricted,
        restrictionReason,
        restrictionHandler: (e) => {
          if (isRestricted && e?.nativeEvent instanceof Event) e.preventDefault();
          if (!isRestricted) return;

          if (isGuest) {
            openModal("Signin", {
              lockedContentOwner: media?.owner,
              lockedReason: CURRENT_USER_LOCKED_LABEL.ROLE_GUEST,
              lockedAction: action,
            });
            return;
          }

          if (ownerDisabledGate) {
            openDrawer(
              <ContentLocked
                restrictionRule={CONTENT_RESTRICTION_RULE.BOOKMARK_MEDIA}
                context={{ ownerDisabledFavorites: true, username: media?.owner?.username }}
                inDrawer
                handleCancel={closeDrawer}
              />,
              { small: true }
            );
            return;
          }

          if (contributorGate) {
            openDrawer(
              <ContentLocked
                restrictionRule={CONTENT_RESTRICTION_RULE.BOOKMARK_MEDIA}
                context={{ contributorGate: true, username: media?.owner?.username }}
                inDrawer
                handleCancel={closeDrawer}
              />,
              { small: true }
            );
            return;
          }

          openDrawer(
            <ContentLocked
              restrictionRule={CONTENT_RESTRICTION_RULE.BOOKMARK_MEDIA}
              context={{ username: media?.owner?.username, caslLocked: true }}
              inDrawer
              handleCancel={closeDrawer}
            />,
            { small: true }
          );
        },
      };
    },
  };

  const restrictionHandler = handlers[action];
  if (restrictionHandler) {
    const currentHandler = restrictionHandler(context);
    if (currentHandler?.isRestricted) return currentHandler;
    return { ...currentHandler, isRestricted: false };
  }

  return { isRestricted: false };
};

export default useRestriction;